import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { adminLogout as apiAdminLogout, auth } from "../lib/api";
import { emailsApi } from "../lib/emailsApi";
import AdminLogin from "./AdminLogin";
import AdminClients from "./AdminClients";
import AdminBilling from "./AdminBilling";
import AdminAffiliates from "./AdminAffiliates";
import AdminAuditLogs from "./AdminAuditLogs";
import AdminPlans from "./AdminPlans";
import AdminSettings from "./AdminSettings";
import AdminSeo from "./AdminSeo";
import AdminStaff from "./AdminStaff";
import AdminLinks from "./AdminLinks";
import AdminEmails from "./AdminEmails";
import { getAdminSession } from "./adminShared";

/**
 * AdminApp — Painel mestre da plataforma Kinng.app.
 * Rota: /admin (super-admin). Mostra módulos da plataforma SaaS para o dono.
 */
const AdminApp = () => {
    const { superAdminLoggedIn, superAdminLogout, registrations } = useSalon();
    const [module, setModule] = useState("clients");
    const [emailUnread, setEmailUnread] = useState(0);

    // Badge de não lidos da caixa de suporte (só super-admin real enxerga o
    // módulo "E-mail"). Poll leve — uma chamada STATUS ao IMAP.
    useEffect(() => {
        if (!superAdminLoggedIn) return;
        const { isStaff } = getAdminSession();
        if (isStaff) return;
        let alive = true;
        const tick = () => emailsApi.unread()
            .then((r) => { if (alive) setEmailUnread(r?.unread || 0); })
            .catch(() => {});
        tick();
        const t = setInterval(tick, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [superAdminLoggedIn]);

    if (!superAdminLoggedIn) return <AdminLogin />;

    const handleLogout = () => {
        // Limpa AMBAS as sessões (admin + tenant/afiliado) do localStorage
        auth.clearAll();
        apiAdminLogout();
        superAdminLogout();
        toast.success("Sessão encerrada");
    };

    // Super-admin real (db.admins) sempre vê tudo. Funcionário (kinng_staff)
    // só vê os módulos cuja permission.view está ligada — "enforce module
    // visibility throughout the session" do módulo Funcionários.
    const { isStaff, permissions, user: adminUser } = getAdminSession();
    const ALL_MODULES = [
        { id: "clients",    label: "Clientes Registrados",  desc: `${registrations.length} cadastros`,       icon: "fa-users",                    permKey: "tenants" },
        { id: "billing",    label: "Faturamento & MRR",     desc: "Receita recorrente",                       icon: "fa-coins",                    permKey: "dashboard" },
        { id: "plans",      label: "Gestão de Planos",      desc: "Comerciais e features",                    icon: "fa-box",                      permKey: "plans" },
        { id: "affiliates", label: "Programa de Afiliados", desc: "Parceiros e comissões",                    icon: "fa-user-group",               permKey: "affiliates" },
        { id: "staff",      label: "Funcionários",          desc: "Equipe interna e permissões",              icon: "fa-users-gear",               permKey: "staff" },
        { id: "links",      label: "Links",                 desc: "URLs importantes pra compartilhar",        icon: "fa-link",                     permKey: "links" },
        { id: "emails",     label: "E-mail",                desc: "Caixa de suporte",                         icon: "fa-envelope",                 permKey: "emails" },
        { id: "domains",    label: "Domínios Próprios",     desc: "Em breve",                                 icon: "fa-globe", disabled: true,    permKey: null },
        { id: "seo",        label: "SEO & Meta",            desc: "Meta tags, OG e negócio local",            icon: "fa-magnifying-glass-chart",   permKey: "seo" },
        { id: "logs",       label: "Logs de Auditoria",     desc: "Timeline de ações críticas",               icon: "fa-file-shield",              permKey: "audit_logs" },
    ];
    const modules = ALL_MODULES.filter((m) => !isStaff || !m.permKey || permissions?.[m.permKey]?.view);
    // Configurações é o último item da navegação principal (mesmo estilo dos
    // outros módulos) — reaproveita a permission "terms" já existente pro
    // Funcionário, já que os dois documentos legais moram nela.
    const settingsVisible = !isStaff || permissions?.terms?.view;
    const settingsItem = { id: "settings", label: "Configurações", desc: "Termos, privacidade e mais", icon: "fa-gear" };
    const navItems = settingsVisible ? [...modules, settingsItem] : modules;
    // `module` pode ter ficado no default "clients" mesmo quando o funcionário
    // não tem permissão pra ele (ex.: login acabou de trocar a sessão cacheada
    // depois do useState já ter montado) — corrige a cada render em vez de
    // depender de um efeito rodando na hora certa.
    const effectiveModule = navItems.some((m) => m.id === module) ? module : modules[0]?.id;

    const handleNav = (item) => {
        if (item.disabled) {
            toast.info("Módulo em desenvolvimento", { description: "Disponível na próxima sprint da plataforma." });
            return;
        }
        setModule(item.id);
    };

    return (
        <div data-testid="admin-app" className="min-h-screen w-full bg-slate-950 text-white flex">
            {/* Sidebar */}
            <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-white/5 bg-slate-950/80 backdrop-blur-xl">
                <div className="px-6 pt-6 pb-5 border-b border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
                            <i className="fa-solid fa-shield-halved text-white"></i>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                Painel Mestre
                            </div>
                            <div className="salon-heading text-base text-white">Kinng.app</div>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
                        <span>{isStaff ? `Funcionário · ${adminUser?.name || "Equipe"}` : "Super-admin · Plataforma SaaS"}</span>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-5 space-y-1 overflow-auto">
                    <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.28em] text-slate-600">
                        Plataforma
                    </div>
                    {navItems.map((item) => {
                        const active = effectiveModule === item.id;
                        return (
                            <button
                                key={item.id}
                                data-testid={`admin-nav-${item.id}`}
                                onClick={() => handleNav(item)}
                                className={`group w-full min-h-[52px] flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${
                                    active
                                        ? "bg-indigo-500/10 border border-indigo-500/30 shadow-lg"
                                        : "hover:bg-white/5 border border-transparent"
                                } ${item.disabled ? "opacity-60" : ""}`}
                            >
                                <div
                                    className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                                        active ? "bg-indigo-500/20 text-indigo-300" : "bg-slate-900/60 text-slate-400"
                                    }`}
                                >
                                    <i className={`fa-solid ${item.icon}`}></i>
                                </div>
                                <div className="text-left flex-1 min-w-0">
                                    <div
                                        className={`text-sm font-medium ${
                                            active ? "text-white" : "text-slate-300 group-hover:text-white"
                                        }`}
                                    >
                                        {item.label}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">{item.desc}</div>
                                </div>
                                {item.id === "emails" && emailUnread > 0 && (
                                    <span
                                        data-testid="admin-nav-emails-badge"
                                        title={`${emailUnread} e-mail${emailUnread === 1 ? "" : "s"} não lido${emailUnread === 1 ? "" : "s"}`}
                                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                                    >
                                        {emailUnread > 9 ? "9+" : emailUnread}
                                    </span>
                                )}
                                {item.disabled && (
                                    <span className="text-[9px] uppercase tracking-wider text-slate-600">
                                        soon
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-white/5 space-y-2">
                    <a
                        href="/"
                        data-testid="admin-go-tenant"
                        className="block w-full min-h-[44px] text-center text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white border border-white/5 hover:border-white/20 rounded-xl py-2.5 transition-all"
                    >
                        <i className="fa-solid fa-store mr-2"></i>Ir para tenant demo
                    </a>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={handleLogout}
                        className="w-full min-h-[44px] text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-red-400 border border-white/5 hover:border-red-500/30 rounded-xl py-2.5 transition-all"
                    >
                        <i className="fa-solid fa-right-from-bracket mr-2"></i>Sair do painel
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                <header className="px-5 lg:px-8 py-5 flex items-center justify-between border-b border-white/5 bg-slate-950/60 backdrop-blur-xl sticky top-0 z-30">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            Plataforma SaaS · Super-Admin
                        </div>
                        <h1 className="salon-heading text-2xl text-white mt-1">
                            Painel Mestre Kinng.app
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10">
                            <i className="fa-solid fa-circle text-indigo-400 text-[8px] animate-pulse"></i>
                            Sessão segura
                        </span>
                        <button
                            data-testid="admin-mobile-logout"
                            onClick={handleLogout}
                            className="lg:hidden text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-red-400 border border-white/5 hover:border-red-500/30 rounded-lg px-3 py-1.5 transition-all"
                        >
                            <i className="fa-solid fa-right-from-bracket mr-1"></i>Sair
                        </button>
                    </div>
                </header>

                {/* Mobile / tablet nav (visível abaixo do lg breakpoint) */}
                <nav
                    data-testid="admin-mobile-nav"
                    className="lg:hidden border-b border-white/5 bg-slate-950/60 backdrop-blur-xl sticky top-[76px] z-20 overflow-x-auto"
                >
                    <div className="flex items-center gap-2 px-4 py-3 min-w-max">
                        {navItems.map((item) => {
                            const active = effectiveModule === item.id;
                            return (
                                <button
                                    key={item.id}
                                    data-testid={`admin-nav-mobile-${item.id}`}
                                    onClick={() => handleNav(item)}
                                    className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-xs transition-all min-h-[40px] ${
                                        active
                                            ? "bg-indigo-500/15 border-indigo-500/40 text-white"
                                            : "border-white/5 text-slate-400 hover:text-white hover:border-white/20"
                                    } ${item.disabled ? "opacity-50" : ""}`}
                                >
                                    <i className={`fa-solid ${item.icon}`}></i>
                                    <span className="whitespace-nowrap">{item.label}</span>
                                    {item.id === "emails" && emailUnread > 0 && (
                                        <span
                                            data-testid="admin-nav-mobile-emails-badge"
                                            className="min-w-[16px] h-[16px] px-1 rounded-full bg-indigo-500 text-white text-[9px] font-bold flex items-center justify-center"
                                        >
                                            {emailUnread > 9 ? "9+" : emailUnread}
                                        </span>
                                    )}
                                    {item.disabled && (
                                        <span className="text-[8px] uppercase tracking-wider text-slate-600">soon</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                <div className="flex-1 p-5 lg:p-7">
                    {effectiveModule === "clients" && <AdminClients />}
                    {effectiveModule === "billing" && <AdminBilling />}
                    {effectiveModule === "plans" && <AdminPlans />}
                    {effectiveModule === "affiliates" && <AdminAffiliates />}
                    {effectiveModule === "staff" && <AdminStaff />}
                    {effectiveModule === "links" && <AdminLinks />}
                    {effectiveModule === "emails" && <AdminEmails />}
                    {effectiveModule === "settings" && <AdminSettings />}
                    {effectiveModule === "seo" && <AdminSeo />}
                    {effectiveModule === "logs" && <AdminAuditLogs />}
                </div>
            </main>
        </div>
    );
};

export default AdminApp;
